import { LibraryItemHoverButton } from '@/components/Library/LibraryItemHoverButton';
import LibraryItemHoverOverlay from '@/components/Library/LibraryItemHoverOverlay';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import PenOutlined from '@/images/PenOutlined.svg';
import { AppRoutes } from '@/utils/types';
import { ITranslationGroup, LibraryItem, LibraryItemStatus, LibraryItemType, TranslationGroupStatus } from '@d-id/types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayModal } from './PlayModal';
import { TranslationOptionsList } from './TranslationOptionsList';

interface Props {
    translationGroup: ITranslationGroup;
    onSelect?: () => void;
    disableSelect?: boolean;
    keepHover: boolean;
    setKeepHover: (value: boolean) => void;
    isExample?: boolean;
    onVideoOpen: () => void;
    showPlay: boolean;
    isEditable?: boolean;
}

const transformStatusToLibraryStatus = (status: TranslationGroupStatus): LibraryItemStatus => {
    const statusAdapter = {
        [TranslationGroupStatus.Created]: LibraryItemStatus.Created,
        [TranslationGroupStatus.Validating]: LibraryItemStatus.Validating,
        [TranslationGroupStatus.PartialDone]: LibraryItemStatus.Done,
        [TranslationGroupStatus.Done]: LibraryItemStatus.Done,
        [TranslationGroupStatus.Error]: LibraryItemStatus.Error,
        [TranslationGroupStatus.Uploading]: LibraryItemStatus.Started,
    };

    return statusAdapter[status];
};

const transformGroupToLibraryItem = (translationGroup: ITranslationGroup & { is_editable?: boolean }): LibraryItem => {
    return {
        id: translationGroup.id,
        created_at: translationGroup.created_at,
        modified_at: translationGroup.modified_at,
        created_by: translationGroup.owner_id,
        video_url: translationGroup.source_url,
        thumbnail_url: translationGroup.thumbnail_url,
        name: translationGroup.name,
        duration: translationGroup.duration,
        subtitles_url: translationGroup.subtitles_url,
        subtitles: !!translationGroup.subtitles_url,
        status: transformStatusToLibraryStatus(translationGroup.status),
        type: LibraryItemType.TRANSLATION,
        target_languages: translationGroup.target_languages,
        is_editable: !!translationGroup.is_editable,
    };
};

export const TranslationContainerHover = ({
    translationGroup,
    onSelect,
    disableSelect,
    keepHover,
    setKeepHover,
    isExample,
    onVideoOpen,
    showPlay,
    isEditable,
}: Props) => {
    const [isVideoWatched, setIsVideoWatched] = useState(false);
    const [isOpen, setOpen] = useState(false);
    const navigate = useNavigate();

    const libraryItem = transformGroupToLibraryItem({ ...translationGroup, is_editable: isEditable });

    const handleOpenVideo = () => {
        onVideoOpen();
        setOpen(true);
    };

    const handleEditDraft = () => {
        navigate(`${AppRoutes.TranslationDraft}/${libraryItem.id}`);
    };

    const handleCloseVideo = () => {
        setOpen(false);
    };

    return (
        <LibraryItemHoverOverlay
            id={isVideoWatched ? 'translation_viewed' : ''}
            show={keepHover}
            className="translation-container-hover"
            dropdown={
                isExample ? null : (
                    <TranslationOptionsList
                        libraryItem={libraryItem}
                        onOpen={() => setKeepHover(true)}
                        onClose={() => setKeepHover(false)}
                    />
                )
            }
            onItemSelect={onSelect}
            disableSelect={disableSelect}>
            {isEditable && (
                <LibraryItemHoverButton
                    onClick={handleEditDraft}
                    color="outlined"
                    icon={<MaskedIcon src={PenOutlined} size="16px" />}
                    text="Edit draft"
                />
            )}
            {showPlay && <LibraryItemHoverButton color="secondary" onClick={handleOpenVideo} text="Play video" />}
            {isOpen && (
                <PlayModal
                    isExample={!!isExample}
                    isOpen={isOpen}
                    handleClose={handleCloseVideo}
                    onPlay={() => {
                        setIsVideoWatched(true);
                    }}
                    libraryItem={libraryItem}
                />
            )}
        </LibraryItemHoverOverlay>
    );
};
